import React, { FC, useEffect, useRef, useState } from 'react'

import clsx from 'clsx'
import { CountryCode, E164Number } from 'libphonenumber-js/core'
import { Phone as PhoneIcon, Plus } from 'lucide-react'
import { Control, Controller, useFieldArray, useWatch } from 'react-hook-form'
import { formatPhoneNumberIntl, isPossiblePhoneNumber } from 'react-phone-number-input'

import PhoneInput from 'Components/PhoneInput'

// Matches the row's leave transition in _phone-list.scss. The row has to finish
// collapsing before react-hook-form drops it, or it would vanish instantly.
const LEAVE_MS = 220

// Where focus goes once a row is gone. A field id means "that row's Remove
// button"; ADD_BUTTON is the fallback when the list has run out of rows.
const ADD_BUTTON = 'add-button'

interface EditState {
  // The value to put back if the edit is abandoned.
  fallback: E164Number | undefined
  id: string
  // Rows created in this session were never saved, so abandoning one deletes
  // the row outright — "Discard" — instead of reverting it.
  isNew: boolean
}

interface Props {
  control: Control
  countryOptionsOrder: CountryCode[]
  defaultCountry: CountryCode
  onPhoneChange(name: string): void
  phoneErrors?: { phone?: { message?: string } }[]
  setValue(name: string, value: E164Number | undefined): void
}

const describe = (phone: E164Number | undefined): string => (phone ? formatPhoneNumberIntl(phone) || String(phone) : '')

// useFieldArray always stamps a row id; the v6 types just don't promise one.
const keyOf = (row: { id?: string } | undefined): string => row?.id ?? ''

export const PhoneList: FC<Props> = ({
  control,
  countryOptionsOrder,
  defaultCountry,
  phoneErrors,
  setValue,
  onPhoneChange,
}) => {
  const {
    append,
    fields: rows,
    remove,
  } = useFieldArray<{ phone: E164Number | undefined }>({
    control,
    name: 'additionalPhones',
  })
  // rows[] holds each entry's value as it was when the array was registered, so
  // the collapsed rows read the live numbers from here instead.
  const values = useWatch<{ phone?: E164Number }[]>({ control, name: 'additionalPhones' })

  const [editing, setEditing] = useState<EditState | null>(null)
  // append() doesn't hand back the row it created, but the row it created is
  // always the last one — which is enough to open it without chasing its id.
  const [adding, setAdding] = useState(false)
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [announcement, setAnnouncement] = useState('')

  const addButtonRef = useRef<HTMLButtonElement>(null)
  const removeButtonRefs = useRef(new Map<string, HTMLButtonElement | null>())
  const pendingFocusRef = useRef<string | null>(null)

  useEffect(() => {
    const focusTarget = pendingFocusRef.current
    if (!focusTarget) return

    pendingFocusRef.current = null
    if (focusTarget === ADD_BUTTON) addButtonRef.current?.focus()
    else removeButtonRefs.current.get(focusTarget)?.focus()
  }, [rows])

  const dropRow = (index: number) => {
    // Focus is sitting on a control that is about to disappear, so claim the
    // neighbouring row's Remove button before the row goes.
    const neighbour = rows[index + 1] ?? rows[index - 1]
    pendingFocusRef.current = neighbour?.id ?? ADD_BUTTON
    removeButtonRefs.current.delete(keyOf(rows[index]))
    remove(index)
  }

  const handleAdd = () => {
    setAdding(true)
    append({ phone: undefined })
  }

  const handleRemove = (index: number) => {
    // index is read again when the timer fires, so a second removal started
    // mid-animation would be working from a list that has already shifted.
    if (removingId) return

    setRemovingId(keyOf(rows[index]))
    window.setTimeout(() => {
      setRemovingId(null)
      dropRow(index)
      setAnnouncement('Phone number removed.')
    }, LEAVE_MS)
  }

  const handleDone = () => {
    setAdding(false)
    setEditing(null)
  }

  const handleAbandon = (index: number, isNew: boolean) => {
    const fallback = editing?.fallback
    handleDone()

    if (isNew) dropRow(index)
    else setValue(`additionalPhones.${index}.phone`, fallback)
  }

  const isEditingAny = adding || editing !== null

  return (
    <>
      <ul className="user-profile-phone-list">
        {rows.map((field, index) => {
          const phone = values?.[index]?.phone ?? field.phone
          const isNew = adding && index === rows.length - 1
          const isEditing = isNew || editing?.id === field.id
          const inputId = `profile-additional-phone-${index}`
          const label = describe(phone)

          return (
            <li
              key={field.id}
              className={clsx(
                'user-profile-phone-row',
                isEditing && 'user-profile-phone-row_editing',
                removingId === field.id && 'user-profile-phone-row_leaving',
              )}
            >
              <div className="user-profile-phone-row__inner">
                {isEditing ? (
                  <Controller
                    control={control}
                    name={`additionalPhones.${index}.phone`}
                    render={(phoneField) => {
                      const draft = phoneField.value as E164Number | undefined
                      const complete = Boolean(draft) && isPossiblePhoneNumber(String(draft))
                      const rawMessage = phoneErrors?.[index]?.phone?.message
                      const error = typeof rawMessage === 'string' ? rawMessage : undefined
                      const errorId = `${inputId}-error`

                      return (
                        <>
                          <div
                            className={clsx(
                              'user-profile-phone-row__field user-profile-field',
                              error && 'user-profile-field_invalid',
                            )}
                          >
                            <label className="sr-only" htmlFor={inputId}>
                              {isNew ? 'New phone number' : 'Phone number'}
                            </label>
                            <PhoneInput
                              {...phoneField}
                              countryOptionsOrder={countryOptionsOrder}
                              defaultCountry={defaultCountry}
                              numberInputProps={{
                                'aria-describedby': error ? errorId : undefined,
                                'aria-invalid': Boolean(error),
                                autoFocus: true,
                                id: inputId,
                              }}
                              onChange={(value) => {
                                phoneField.onChange(value)
                                onPhoneChange(`additionalPhones.${index}.phone`)
                              }}
                            />
                            {error ? (
                              <p className="user-profile-field__error" id={errorId} role="alert">
                                {error}
                              </p>
                            ) : null}
                          </div>
                          <div className="user-profile-phone-row__actions">
                            <button
                              className="user-profile-link-button"
                              disabled={!complete}
                              type="button"
                              onClick={handleDone}
                            >
                              Done
                            </button>
                            <button
                              className="user-profile-link-button user-profile-link-button_quiet"
                              type="button"
                              onClick={() => handleAbandon(index, isNew)}
                            >
                              {isNew ? 'Discard' : 'Cancel'}
                            </button>
                          </div>
                        </>
                      )
                    }}
                  />
                ) : (
                  <>
                    <div className="user-profile-phone-row__value">
                      <span aria-hidden="true" className="user-profile-icon">
                        <PhoneIcon size={16} strokeWidth={1.75} />
                      </span>
                      <span className="user-profile-phone-row__number">{label || 'No number'}</span>
                    </div>
                    <div className="user-profile-phone-row__actions">
                      <button
                        aria-label={label ? `Edit ${label}` : 'Edit phone number'}
                        className="user-profile-link-button"
                        type="button"
                        onClick={() => setEditing({ fallback: phone, id: keyOf(field), isNew: false })}
                      >
                        Edit
                      </button>
                      <button
                        ref={(node) => {
                          removeButtonRefs.current.set(keyOf(field), node)
                        }}
                        aria-label={label ? `Remove ${label}` : 'Remove phone number'}
                        className="user-profile-link-button user-profile-link-button_danger"
                        type="button"
                        onClick={() => handleRemove(index)}
                      >
                        Remove
                      </button>
                    </div>
                  </>
                )}
              </div>
            </li>
          )
        })}
      </ul>

      {/* One row is open at a time, so adding is held back until the open one
          is either finished or abandoned. */}
      <button
        ref={addButtonRef}
        className="user-profile-link-button"
        disabled={isEditingAny}
        type="button"
        onClick={handleAdd}
      >
        <Plus size={16} strokeWidth={2} />
        Add phone number
      </button>

      <p aria-live="polite" className="sr-only" role="status">
        {announcement}
      </p>
    </>
  )
}
